/**
 * Device Detection & Platform Identification
 * 
 * ARCHITECTURE:
 * - Single source of truth for platform detection
 * - Consumes DCW_PLATFORM_OVERRIDE from index.html (handles SDK timing)
 * - No internal caching (index.html already handles this)
 * - Performance-optimized for game loop (minimal overhead)
 * 
 * DESIGN PHILOSOPHY:
 * - Functions are pure and testable
 * - No hidden state or side effects
 * - Direct feature detection over heuristics
 * - AAA game dev standards: clean, fast, maintainable
 * 
 * @module engine/device
 * @author UNBRANDED
 */

// ============================================================================
// PLATFORM CONSTANTS
// ============================================================================

/**
 * Canonical platform identifiers
 * @readonly
 * @enum {string}
 */
export const PLATFORM = {
  DESKTOP: 'desktop',
  MOBILE: 'mobile',
  FARCASTER: 'farcaster',
  BASE_MINIKIT: 'base_minikit',
  WORLDCOIN: 'worldcoin',
  COINBASE: 'coinbase',
  UNKNOWN: 'unknown'
};

// ============================================================================
// PLATFORM DETECTION (Single Source of Truth)
// ============================================================================

/**
 * Detect current platform environment
 * 
 * DETECTION HIERARCHY:
 * 1. Check DCW_PLATFORM_OVERRIDE from index.html (handles SDK injection timing)
 * 2. Check window globals (SDK presence)
 * 3. Check User Agent strings
 * 4. Check viewport/device characteristics
 * 5. Default to desktop
 * 
 * PERFORMANCE:
 * - No internal caching (index.html handles this via DCW_PLATFORM_OVERRIDE)
 * - Early returns for fast path
 * - Minimal DOM queries
 * 
 * @returns {string} Platform identifier from PLATFORM enum
 * 
 * @example
 * const platform = detectPlatform();
 * if (platform === PLATFORM.FARCASTER) {
 *   // Use Farcaster SDK
 * }
 */
export function detectPlatform() {
  // SSR/Test guard
  if (typeof window === 'undefined' || typeof navigator === 'undefined') {
    return PLATFORM.UNKNOWN;
  }

  const localHost = /^(localhost|127\.0\.0\.1|\[::1\])$/.test(window.location.hostname);
  const explicitPlatform = new URLSearchParams(window.location.search).get('platform');
  const isTestEnv = import.meta.env?.MODE === 'test' || globalThis.DCW_TEST_MODE;
  if (localHost && !explicitPlatform && !isTestEnv) {
    return PLATFORM.DESKTOP;
  }

  // PRIORITY 1: Use override from index.html (handles SDK injection timing)
  // This is the SINGLE SOURCE OF TRUTH set by early detection
  const override = window.DCW_PLATFORM_OVERRIDE;
  if (override) {
    // Map override values to PLATFORM constants
    switch (override) {
      case 'farcaster': return PLATFORM.FARCASTER;
      case 'base-minikit': return PLATFORM.BASE_MINIKIT;
      case 'worldcoin': return PLATFORM.WORLDCOIN;
      default: break;
    }
  }

  // PRIORITY 2: Direct SDK detection (fallback if index.html didn't detect)
  const ua = navigator.userAgent || '';

  // Farcaster: Check for SDK or iframe context
  if (
    window.farcaster ||
    window.sdk ||
    window.__farcaster_miniapp__ ||
    (window.miniKit?.isFrameContext === true) ||
    /warpcast|farcaster/i.test(ua + document.referrer)
  ) {
    return PLATFORM.FARCASTER;
  }

  // Base App: MiniKit/base.app only. A plain Coinbase extension is not Base.
  const eth = window.ethereum;
  const baseMini = !!(window.baseMiniKit || eth?.isBaseMiniKit);
  const baseUrl = /base\.app/i.test(window.location.href + document.referrer);
  if (baseMini || baseUrl) {
    return PLATFORM.BASE_MINIKIT;
  }

  // World App: Check for World SDK or User Agent
  // NOTE: World MiniKit v2 doesn't expose window.WorldApp automatically
  // Detection must rely on User Agent and referrer
  if (
    /world\s*app|worldcoin|worldid/i.test(ua) ||
    /world\s*app|worldcoin|worldid/i.test(document.referrer || '') ||
    window.worldcoin ||
    window.WorldApp ||
    window.world?.isWorldApp
  ) {
    return PLATFORM.WORLDCOIN;
  }

  // Coinbase Wallet (not Base App)
  if (
    window.ethereum?.isCoinbaseWallet &&
    !window.ethereum?.isBase &&
    !window.baseMiniKit
  ) {
    return PLATFORM.COINBASE;
  }

  // Mobile device detection
  if (isMobileDevice()) {
    return PLATFORM.MOBILE;
  }

  // Default: Desktop
  return PLATFORM.DESKTOP;
}

/**
 * Force platform re-detection by clearing override flag
 * 
 * USE CASE: When SDKs inject after page load (e.g., Base App delayed injection)
 * 
 * PERFORMANCE: O(1) - just clears a flag
 * 
 * @returns {string} Newly detected platform
 * 
 * @example
 * // After SDK loads asynchronously
 * window.addEventListener('base-sdk-loaded', () => {
 *   const newPlatform = forcePlatformRedetection();
 *   console.log('Platform updated to:', newPlatform);
 * });
 */
export function forcePlatformRedetection() {
  if (typeof window !== 'undefined') {
    // Clear override to force fresh detection
    delete window.DCW_PLATFORM_OVERRIDE;
    
    // Dispatch event to notify components (if dispatchEvent available)
    try {
      if (typeof window.dispatchEvent === 'function') {
        window.dispatchEvent(new CustomEvent('dcw-platform-redetected', {
          detail: { platform: detectPlatform() }
        }));
      }
    } catch (e) {
      // Ignore in test environments without full DOM API
    }
  }
  
  return detectPlatform();
}

/**
 * Emergency reset for platform detection
 * 
 * USE CASE: When app detects rendering issues (black screen, wrong layout)
 * TRIGGERS: User action or automatic watchdog
 * 
 * @returns {Object} Current detection state
 * 
 * @example
 * // In CRT shader watchdog
 * if (blackScreenDetected) {
 *   const { platform, isMobile } = emergencyMobileDetectionReset();
 *   console.warn('Emergency reset:', platform, isMobile);
 * }
 */
export function emergencyMobileDetectionReset() {
  if (typeof window === 'undefined') {
    return { platform: PLATFORM.UNKNOWN, isMobile: false };
  }

  // Clear override and force re-detection
  delete window.DCW_PLATFORM_OVERRIDE;
  
  const platform = detectPlatform();
  const isMobile = isMobileDevice();

  // Notify components (if dispatchEvent available)
  try {
    if (typeof window.dispatchEvent === 'function') {
      window.dispatchEvent(new CustomEvent('dcw-platform-reset', {
        detail: { platform, isMobile, emergency: true }
      }));
    }
  } catch (e) {
    // Ignore in test environments without full DOM API
  }

  return { platform, isMobile };
}

// ============================================================================
// MOBILE DEVICE DETECTION
// ============================================================================

/**
 * Detect if current device is mobile hardware
 * 
 * METHODOLOGY:
 * - Uses User Agent string (most reliable for actual mobile devices)
 * - Does NOT use viewport size (desktops can have small viewports)
 * - Does NOT use touch support (desktops have touchscreens)
 * 
 * PERFORMANCE: O(1) regex check
 * 
 * @returns {boolean} True if mobile device hardware
 * 
 * @example
 * const isMobile = isMobileDevice();
 * if (isMobile) {
 *   // Use mobile-optimized UI
 * }
 */
export function isMobileDevice() {
  // SSR/Test guard
  if (typeof navigator === 'undefined') {
    return false;
  }

  // Direct UA check (most reliable)
  const ua = navigator.userAgent || '';
  return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(ua);
}

// ============================================================================
// GPU CAPABILITY DETECTION (CRT Shader Optimization)
// ============================================================================

/**
 * Detect GPU capabilities for shader complexity selection
 * 
 * RETURNS:
 * - 'high': Powerful GPU (desktop, modern hardware)
 * - 'medium': Average GPU (most devices)
 * - 'low': Limited GPU (older mobile, integrated graphics)
 * 
 * CACHING:
 * - Stores result in sessionStorage (expensive WebGL detection)
 * - Cache invalidated on page reload
 * 
 * PERFORMANCE:
 * - First call: ~10-50ms (WebGL context creation)
 * - Subsequent calls: <1ms (sessionStorage read)
 * 
 * @returns {string} GPU capability level
 * 
 * @example
 * const gpuLevel = detectGPUCapability();
 * const shaderComplexity = {
 *   high: 'full_effects',
 *   medium: 'balanced',
 *   low: 'performance'
 * }[gpuLevel];
 */
export function detectGPUCapability() {
  // SSR/Test guard
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return 'medium';
  }

  // Check cache (WebGL context creation is expensive)
  try {
    const cached = sessionStorage.getItem('dcw_gpu_capability');
    if (cached) return cached;
  } catch (e) {
    // sessionStorage unavailable (private mode, etc.)
  }

  let result;

  try {
    // Create temporary WebGL context
    const canvas = document.createElement('canvas');
    const gl = canvas.getContext('webgl2') || 
               canvas.getContext('webgl') || 
               canvas.getContext('experimental-webgl');

    if (!gl) {
      result = 'low'; // No WebGL support
    } else {
      // Get GPU renderer string
      const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
      const renderer = debugInfo 
        ? gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL) 
        : '';

      // Detect high-end GPUs
      const isHighEnd = /RTX|GTX|Radeon\s+RX|AMD\s+Radeon\s+Pro|Intel\s+Iris/i.test(renderer);
      
      // Detect low-end GPUs
      const isLowEnd = /Intel\s+HD\s+Graphics|Mali-[GT]\d{3}|PowerVR|Adreno\s+[345]\d{2}/i.test(renderer);
      
      // Check device type
      const isMobile = isMobileDevice();
      
      // Check WebGL2 support (indicator of modern hardware)
      const hasWebGL2 = canvas.getContext('webgl2') !== null;
      
      // Determine capability
      if (isHighEnd && !isMobile) {
        result = 'high';
      } else if (isLowEnd || (!hasWebGL2 && isMobile)) {
        result = 'low';
      } else {
        result = 'medium';
      }

      // Check texture size limits (additional indicator)
      const maxTextureSize = gl.getParameter(gl.MAX_TEXTURE_SIZE);
      if (maxTextureSize < 4096) {
        // Downgrade if texture limits are low
        result = result === 'high' ? 'medium' : 'low';
      }

      // Release WebGL context to avoid context limit issues
      try {
        const loseCtxExt = gl.getExtension('WEBGL_lose_context');
        loseCtxExt?.loseContext();
      } catch (e) {
        // Ignore - not critical
      }
    }

    // Cache result
    try {
      sessionStorage.setItem('dcw_gpu_capability', result);
    } catch (e) {
      // sessionStorage unavailable - continue without cache
    }

  } catch (e) {
    console.warn('[Device] GPU detection failed:', e.message);
    result = 'medium'; // Safe fallback
  }

  return result;
}

// ============================================================================
// DEBUG UTILITIES (Development only)
// ============================================================================

/**
 * Global debugging function for platform detection
 * Available in browser console: window.debugSDKDetection()
 */
if (typeof window !== 'undefined') {
  window.debugSDKDetection = function () {
    const platform = detectPlatform();
    const isMobile = isMobileDevice();
    const gpuLevel = detectGPUCapability();

    console.log('🔍 DCW PLATFORM DETECTION DEBUG');
    console.log('================================');
    console.log('📍 Location:', window.location.href);
    console.log('🔗 Referrer:', document.referrer || '(none)');
    console.log('🖥️  User Agent:', navigator.userAgent);
    console.log('');
    console.log('🎯 DETECTED PLATFORM:', platform);
    console.log('📱 Is Mobile Device:', isMobile);
    console.log('🎮 GPU Capability:', gpuLevel);
    console.log('');
    console.log('🌐 SDK Globals:');
    console.log('  window.farcaster:', !!window.farcaster);
    console.log('  window.miniKit:', !!window.miniKit);
    console.log('  window.miniKit.isFrameContext:', window.miniKit?.isFrameContext);
    console.log('  window.baseMiniKit:', !!window.baseMiniKit);
    console.log('  window.worldcoin:', !!window.worldcoin);
    console.log('  window.ethereum:', !!window.ethereum);
    console.log('  window.ethereum.isBase:', window.ethereum?.isBase);
    console.log('  window.ethereum.isCoinbaseWallet:', window.ethereum?.isCoinbaseWallet);
    console.log('');
    console.log('⚙️  Overrides:');
    console.log('  DCW_PLATFORM_OVERRIDE:', window.DCW_PLATFORM_OVERRIDE || '(none)');
    console.log('  DCW_PROFILE_OVERRIDE:', window.DCW_PROFILE_OVERRIDE || '(none)');

    return {
      platform,
      isMobile,
      gpuLevel,
      overrides: {
        platform: window.DCW_PLATFORM_OVERRIDE,
        profile: window.DCW_PROFILE_OVERRIDE
      }
    };
  };
}

// ============================================================================
// EXPORTS
// ============================================================================

export default {
  PLATFORM,
  detectPlatform,
  isMobileDevice,
  detectGPUCapability,
  forcePlatformRedetection,
  emergencyMobileDetectionReset
};
